import React, { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { sanitizeInput } from "@/utils/helpers";
import { INPUT_LIMITS } from "@/utils/constants";
import { VoiceButton } from "@/components/ui/VoiceButton";
import { AudioWaveform } from "@/components/ui/AudioWaveform";

export const ChatInput = ({
  onSendMessage,
  isLoading,
  disabled,
  gpcService,
  showBranding = true,
}) => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [analyser, setAnalyser] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!disabled && !isLoading && !isRecording) {
      inputRef.current?.focus();
    }
  }, [disabled, isLoading, isRecording]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${Math.min(
        inputRef.current.scrollHeight,
        120,
      )}px`;
    }
  }, [input]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedInput = sanitizeInput(input);

    if (!trimmedInput || isLoading || disabled || isRecording) {
      return;
    }

    try {
      await onSendMessage(trimmedInput);
      setInput("");
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isRecording) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleVoiceTranscript = async (audioBlob) => {
    if (!gpcService) {
      throw new Error(t("toast.gpcServiceNotAvailable"));
    }

    const response = await gpcService.transcribeAudio(audioBlob);

    if (response.success && response.text) {
      setInput(response.text);
      inputRef.current?.focus();
    }
  };

  const handleRecordingStateChange = (recording) => {
    setIsRecording(recording);
  };

  const handleAnalyserChange = (newAnalyser) => {
    setAnalyser(newAnalyser);
  };

  const canSend =
    input.trim().length > 0 && !isLoading && !disabled && !isRecording;
  const inputDisabled = isLoading || disabled || isRecording;
  const voiceDisabled = disabled || input.trim().length > 0;

  return (
    <form onSubmit={handleSubmit} className="gpc-input-container">
      <div className="gpc-input-wrapper">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isRecording ? "" : t("chat.placeholder")}
          disabled={inputDisabled}
          maxLength={INPUT_LIMITS.MAX_LENGTH}
          className={`gpc-input gpc-textarea ${
            isRecording ? "gpc-input-recording" : ""
          }`}
          aria-label={t("chat.placeholder")}
          aria-describedby="chat-input-help"
          rows={1}
        />
        <AudioWaveform analyser={analyser} isRecording={isRecording} />
      </div>

      <div className="gpc-input-actions-right">
        <VoiceButton
          onTranscript={handleVoiceTranscript}
          isLoading={isLoading}
          disabled={voiceDisabled}
          onRecordingStateChange={handleRecordingStateChange}
          onAnalyserChange={handleAnalyserChange}
        />

        <button
          type="submit"
          disabled={!canSend}
          className="gpc-send-button"
          aria-label={t("chat.send")}
          title={t("chat.send")}
        >
          {isLoading ? (
            <span className="gpc-spinner" />
          ) : (
            <Send className="gpc-icon" />
          )}
        </button>
      </div>

      <div className="gpc-footer-container">
        <span className="gpc-ai-disclaimer">{t("chat.aiDisclaimer")}</span>
        {showBranding && (
          <span className="gpc-powered-by">{t("widget.poweredBy")}</span>
        )}
      </div>

      <span id="chat-input-help" className="gpc-sr-only">
        {t("chat.placeholder")}.{" "}
        {t("input.maxCharacters", { count: INPUT_LIMITS.MAX_LENGTH })}
      </span>
    </form>
  );
};
